// ============================================================================
// Sheet — a mobile-first bottom sheet (rises from the bottom on phones, becomes
// a centered dialog on ≥sm). Local to the sessions page; built on the locked
// Pulse tokens (surface / line / radii / shadows) without touching any shared
// component. Focus-trapped enough for the flows here (Escape + outside-click +
// initial focus), scroll-locked, and reduced-motion aware.
// ============================================================================
import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/cn";

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Sticky footer (e.g. the save bar). Stays pinned while the body scrolls. */
  footer?: ReactNode;
  children: ReactNode;
  /** Constrain the desktop dialog width. Defaults to a comfortable form width. */
  size?: "md" | "lg";
}

const EASE = [0.22, 0.61, 0.36, 1] as const;

export function Sheet({
  open,
  onClose,
  title,
  subtitle,
  footer,
  children,
  size = "md",
}: SheetProps) {
  const { t } = useTranslation("sessions");
  const reduce = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);

  // Scroll-lock the page + Escape-to-close + initial focus while open.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    // Focus the first focusable element so keyboard users land inside.
    const id = window.setTimeout(() => {
      const focusable = panelRef.current?.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      focusable?.focus();
    }, 60);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
      window.clearTimeout(id);
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-label={typeof title === "string" ? title : undefined}
        >
          {/* Scrim */}
          <motion.div
            className="absolute inset-0 bg-ink/40 backdrop-blur-[2px]"
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            ref={panelRef}
            className={cn(
              "relative flex max-h-[92dvh] w-full flex-col overflow-hidden bg-surface shadow-lg",
              "rounded-t-xl sm:rounded-xl sm:max-h-[88dvh]",
              size === "lg" ? "sm:max-w-2xl" : "sm:max-w-lg",
            )}
            initial={
              reduce ? { opacity: 0 } : { opacity: 0, y: 40, scale: 0.99 }
            }
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: 40, scale: 0.99 }}
            transition={{ duration: 0.28, ease: EASE }}
          >
            {/* Grab handle (phones) */}
            <div className="pt-2 sm:hidden" aria-hidden>
              <div className="mx-auto h-1.5 w-10 rounded-full bg-line-strong" />
            </div>

            {/* Header */}
            <div className="flex items-start justify-between gap-4 px-5 pb-3 pt-3 sm:px-6 sm:pt-5">
              <div className="min-w-0">
                <h2 className="font-display text-xl font-extrabold tracking-tightest text-body">
                  {title}
                </h2>
                {subtitle && (
                  <p className="mt-0.5 text-sm text-body-soft">{subtitle}</p>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label={t("close")}
                className="-mr-1 grid h-9 w-9 shrink-0 place-items-center rounded-md text-body-muted transition-colors hover:bg-surface-sunken hover:text-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
                  <path
                    d="M6 6l12 12M18 6 6 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            {/* Scrollable body */}
            <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4 sm:px-6">
              {children}
            </div>

            {/* Sticky footer */}
            {footer && (
              <div className="border-t border-line bg-surface/95 px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur sm:px-6">
                {footer}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
